import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faCopy,
  faEllipsisVertical,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer } from "react-toastify";
import { toastify } from "../../../components/Toast";
import "./KindleDetail.scss";

const classNames = (...classes) => classes.filter(Boolean).join(" ");

const KindleDetail = () => {
  const { id: bookId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [kindle, setKindleDetail] = useState({
    bookDetail: null,
    bookHighlights: [],
  });
  const [cardFilterDefault, setCardFilterDefault] = useState("Location");
  const [copiedId, setCopiedId] = useState(null);

  const filterDropDown = [
    { name: "Location", value: "Location" },
    { name: "Newest Highlights", value: "Newest_Highlights" },
    { name: "Oldest Highlights", value: "Oldest_Highlights" },
  ];

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !bookId) return;

    const fetchHighlights = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}books/highlights-in-book?bookId=${bookId}&filterValue=${cardFilterDefault}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setKindleDetail(response?.data?.data || { bookDetail: null, bookHighlights: [] });
      } catch (error) {
        console.error("Error fetching Kindle highlights:", error);
        toastify("error", error?.response?.data?.message || "Unable to load Kindle highlights");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHighlights();
  }, [bookId, cardFilterDefault]);

  const handleFilterText = (value) => {
    if (value !== cardFilterDefault) setCardFilterDefault(value);
  };

  const copyHighlight = async (highlightId, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(highlightId);
      toastify("success", "Highlight copied successfully");
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Unable to copy Kindle highlight:", error);
      toastify("error", "Unable to copy this highlight");
    }
  };

  const toggleEmailReminder = async () => {
    const book = kindle?.bookDetail;
    if (!book) return;

    const nextFocusState = !book.isFocused;
    setKindleDetail((current) => ({
      ...current,
      bookDetail: { ...current.bookDetail, isFocused: nextFocusState },
    }));

    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}books/update`,
        { bookId: book._id, isFocused: nextFocusState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
    } catch (error) {
      setKindleDetail((current) => ({
        ...current,
        bookDetail: { ...current.bookDetail, isFocused: Boolean(book.isFocused) },
      }));
      console.error("Error updating daily email preference:", error);
      toastify("error", error?.response?.data?.message || "Unable to update daily emails");
    }
  };

  const book = kindle?.bookDetail;
  const highlights = kindle?.bookHighlights || [];

  return (
    <>
      {isLoading ? (
        <div className="App-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      ) : (
        <section className="kindle-detail-page">
          <header className="kindle-detail-topbar">
            <div className="kindle-detail-heading">
              <a href="/app/kindleList" className="kindle-library-link">
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Library</span>
              </a>
              <div className="kindle-detail-title-group">
                <h1 title={book?.title || "Kindle Highlights"}>{book?.title || "Kindle Highlights"}</h1>
                <span className="kindle-detail-count">{highlights.length}</span>
              </div>
            </div>
          </header>

          <main className="kindle-detail-content">
            {book ? (
              <>
                <section className="kindle-book-banner">
                  <div className="kindle-detail-cover">
                    <img src={book.image} alt={`${book.title} cover`} />
                  </div>

                  <div className="kindle-book-information">
                    <p className="kindle-detail-author">{book.authorName}</p>
                    <h2>{book.title}</h2>
                    <div className="kindle-book-metadata">
                      <span className="kindle-highlights-badge">{highlights.length} highlights</span>
                      <button
                        type="button"
                        className={classNames("kindle-email-reminder", book.isFocused && "is-active")}
                        onClick={toggleEmailReminder}
                        aria-pressed={Boolean(book.isFocused)}
                      >
                        <span className="kindle-email-reminder-icon">
                          <FontAwesomeIcon icon={faEnvelope} />
                          {book.isFocused && <i />}
                        </span>
                        {book.isFocused ? "Daily emails active" : "Activate daily emails"}
                      </button>
                      <span className="kindle-last-accessed">
                        Last accessed {book.lastHighlightDate ? moment(book.lastHighlightDate).format("MMM D, YYYY") : "---"}
                      </span>
                    </div>
                  </div>

                  <Menu as="div" className="kindle-detail-menu">
                    <Menu.Button className="kindle-detail-menu-trigger" aria-label="Kindle highlight options">
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="kindle-detail-dropdown">
                        {filterDropDown.map((item) => (
                          <Menu.Item key={item.value}>
                            {({ active }) => (
                              <button
                                type="button"
                                className={classNames(active && "is-hovered", cardFilterDefault === item.value && "is-selected")}
                                onClick={() => handleFilterText(item.value)}
                              >
                                {item.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </section>

                {highlights.length ? (
                  <section className="kindle-highlights-grid" aria-label="Book highlights">
                    {highlights.map((highlight, index) => {
                      const isLarge = index % 5 === 0;
                      const isEmphasized = index % 7 === 0;

                      return (
                        <article
                          key={highlight._id}
                          className={classNames(
                            "kindle-highlight-card",
                            isLarge && "is-large",
                            isEmphasized && "is-emphasized"
                          )}
                        >
                          <div className="kindle-highlight-toolbar">
                            <span className="kindle-location-badge">Loc {highlight.location}</span>
                            <button
                              type="button"
                              className="kindle-copy-highlight"
                              onClick={() => copyHighlight(highlight._id, highlight.text)}
                              aria-label={copiedId === highlight._id ? "Highlight copied" : "Copy highlight"}
                              title={copiedId === highlight._id ? "Copied" : "Copy highlight"}
                            >
                              <FontAwesomeIcon icon={copiedId === highlight._id ? faCheck : faCopy} />
                            </button>
                          </div>
                          {/* <a href={`/app/kindledetail/highlight/${highlight._id}`} className="kindle-highlight-text">
                            {highlight.text}
                          </a> */}
                          <p className="kindle-highlight-text">
                            {highlight.text}
                          </p>
                          {isEmphasized && <span className="kindle-highlight-accent" aria-hidden="true" />}
                        </article>
                      );
                    })}
                  </section>
                ) : (
                  <div className="kindle-highlights-empty">No highlights found for this book.</div>
                )}
              </>
            ) : (
              <div className="kindle-highlights-empty">Book not found.</div>
            )}
          </main>
        </section>
      )}
      <ToastContainer />
    </>
  );
};

export default KindleDetail;
